import React, {useEffect, useState} from 'react';
import {App, Button, Popconfirm, Spin, Table, Tooltip} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {simulateTradingService} from '../../services/simulateService';
import type {SimulatePosition} from '../../types/simulate';


interface PositionTableProps {
    autoRefreshInterval?: number;
    refreshKey?: number;
}

/**
 * PositionTable - 模拟交易当前持仓组件
 *
 * 用于展示用户的模拟合约持仓列表。
 * 已重构为暗黑主题样式，并适配为 Tabs 的一个子面板。
 *
 * @author page
 * @date 2026-04-22
 */
const PositionTable: React.FC<PositionTableProps> = ({
                                                         autoRefreshInterval = 60000,
                                                         refreshKey,
                                                     }) => {
    const {message: messageApi} = App.useApp();
    const [positions, setPositions] = useState<SimulatePosition[]>([]);
    const [loading, setLoading] = useState(false);
    const [closingId, setClosingId] = useState<number | null>(null);

    const loadPositions = async (silent = false) => {
        try {
            if (!silent) setLoading(true);
            const res = await simulateTradingService.getPositions();
            setPositions(res.data?.data || []);
        } catch (error: any) {
            // ignore
        } finally {
            if (!silent) setLoading(false);
        }
    };

    useEffect(() => {
        loadPositions();

        let timer: ReturnType<typeof setInterval> | null = null;
        if (autoRefreshInterval > 0) {
            timer = setInterval(() => {
                loadPositions(true);
            }, autoRefreshInterval);
        }
        return () => {
            if (timer) clearInterval(timer);
        };
    }, [autoRefreshInterval, refreshKey]);

    const handleClose = async (positionId: number) => {
        try {
            setClosingId(positionId);
            await simulateTradingService.closePosition(positionId);
            messageApi.success('平仓成功');
            loadPositions();
        } catch (error: any) {
            messageApi.error(error.response?.data?.message || error.message || '平仓失败');
        } finally {
            setClosingId(null);
        }
    };

    const columns: ColumnsType<SimulatePosition> = [
        {
            title: '交易对',
            key: 'symbol',
            width: 130,
            fixed: 'left',
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (_, record) => {
                const isLong = 0 === record.side;
                const isContract = 1 === record.mode;
                return (
                    <span style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                        <span style={{
                            color: isLong ? 'var(--pt-bull)' : 'var(--pt-bear)',
                            fontWeight: 500,
                        }}>{record.symbol}</span>
                        {isContract && <span style={{
                            fontSize: '10px',
                            fontWeight: 'normal',
                            color: 'var(--pt-primary)',
                            background: 'rgba(240, 180, 41, 0.15)',
                            padding: '1px 4px',
                            borderRadius: '2px',
                            marginLeft: '4px',
                        }}>永续</span>}
                    </span>
                );
            },
        },
        {
            title: '杠杆',
            dataIndex: 'leverage',
            key: 'leverage',
            width: 60,
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (val: number) => <span
                style={{
                    color: 'var(--pt-primary)',
                    fontWeight: 'bold',
                    display: 'block',
                    textAlign: 'center'
                }}>{val}x</span>,
        },
        {
            title: '持仓量',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 100,
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (val: string) => <span
                style={{
                    color: '#fff',
                    fontFamily: 'Monaco',
                    display: 'block',
                    textAlign: 'right'
                }}>{Number(val).toFixed(4)}</span>,
        },
        {
            title: '开仓均价',
            dataIndex: 'avgPrice',
            key: 'avgPrice',
            width: 100,
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (val: string) => <span
                style={{
                    color: '#fff',
                    fontFamily: 'Monaco',
                    display: 'block',
                    textAlign: 'right'
                }}>${Number(val).toFixed(2)}</span>,
        },
        {
            title: '持仓成本',
            key: 'positionCost',
            width: 120,
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (_, record) => {
                const qty = Number(record.quantity);
                const avgPx = Number(record.avgPrice);
                const lev = record.leverage || 1;
                const tradeFee = Number(record.cumTradeFee || 0);
                const cost = 0 === record.mode
                    ? avgPx * qty + tradeFee
                    : avgPx * qty / lev + tradeFee + Number(record.cumFundingFee || 0);
                return <span style={{
                    color: '#fff',
                    fontFamily: 'Monaco',
                    display: 'block',
                    textAlign: 'right'
                }}>${cost.toFixed(2)}</span>;
            },
        },
        {
            title: '强平价格',
            dataIndex: 'liquidationPrice',
            key: 'liquidationPrice',
            width: 100,
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (val: string) => val ?
                <span style={{
                    color: 'var(--pt-bear)',
                    fontFamily: 'Monaco',
                    display: 'block',
                    textAlign: 'right'
                }}>${Number(val).toFixed(2)}</span> : '-',
        },
        {
            title: (
                <span>
                    未结盈亏
                    <Tooltip title="模拟交易每分钟结算一次">
                        <InfoCircleOutlined style={{marginLeft: 4, color: 'var(--pt-text-secondary)', fontSize: 12}}/>
                    </Tooltip>
                </span>
            ),
            dataIndex: 'unrealizedPnl',
            key: 'unrealizedPnl',
            width: 120,
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (val: string, record: any) => {
                const num = Number(val);
                const pct = Number(record.profitPct || 0);
                const isPositive = num >= 0;
                const color = isPositive ? 'var(--pt-bull)' : 'var(--pt-bear)';
                return (
                    <span style={{color, fontWeight: 500, fontFamily: 'Monaco', display: 'block', textAlign: 'right'}}>
                        {isPositive ? '+' : ''}{num.toFixed(2)}({isPositive ? '+' : ''}{pct.toFixed(2)}%)
                    </span>
                );
            },
        },
        {
            title: '操作',
            key: 'action',
            width: 80,
            fixed: 'right',
            onHeaderCell: () => ({style: {textAlign: 'center'}}),
            render: (_, record) => {
                const isProfit = Number(record.unrealizedPnl) >= 0;
                const btnColor = isProfit ? 'var(--pt-bull)' : 'var(--pt-bear)';
                return (
                    <Popconfirm
                        title={<span style={{color: '#fff'}}>确定要市价平仓吗？</span>}
                        onConfirm={() => handleClose(record.id!)}
                        okText="确定"
                        cancelText="取消"
                        classNames={{root: 'dark-theme-popover'}}
                    >
                        <Button
                            size="small"
                            loading={closingId === record.id}
                            style={{
                                padding: '2px 8px',
                                fontSize: 11,
                                background: `${btnColor}4D`,
                                color: btnColor,
                                border: '1px solid transparent',
                                borderRadius: 3,
                                lineHeight: '20px',
                                height: 24,
                            }}
                        >
                            市价平仓
                        </Button>
                    </Popconfirm>
                );
            },
        },
    ];

    // 自定义表格样式覆盖，适配暗黑主题
    const darkTableStyle = {
        style: {
            backgroundColor: 'var(--pt-bg-panel)',
            color: '#fff'
        }
    };

    return (
        <Spin spinning={loading && positions.length === 0}>
            <Table
                className="dark-theme-table"
                columns={columns}
                dataSource={positions}
                rowKey="id"
                pagination={false}
                scroll={{x: 900, y: 400}}
                locale={{emptyText: '暂无持仓'}}
                size="small"
                onRow={() => darkTableStyle}
            />
        </Spin>
    );
};

export default PositionTable;